import { AuthError, AuthSessionMissingError, createClient, type Session } from "@supabase/supabase-js";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "";
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "";
const storageKey = `sb-${new URL(supabaseUrl).hostname.split(".")[0]}-auth-token`;
const memory = new Map<string, string>();
const listeners = new Set<() => void>();
let terminated = false;
let logoutToken: string | null = null;
let storageError: AuthError | null = null;
let hasAcceptedSession = false;
let observedSavedSession = false;

// Own the fallback too: removing localStorage alone cannot clear the SDK's
// otherwise private in-memory session when browser storage is unavailable.
const persistentStorage = (() => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
})();
// Failed writes require memory sessions, but older saved credentials still
// need the readable/removable browser storage handle during sign-out.
const canPersistSession = (() => {
  if (!persistentStorage) { return false; }
  try {
    const probe = `${storageKey}-storage-test-${Math.random()}`;
    persistentStorage.setItem(probe, probe);
    persistentStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
})();

const bestEffort = (cleanup: () => unknown) => {
  try {
    void Promise.resolve(cleanup()).catch(() => undefined);
  } catch {
    // Local state and storage removal never depend on SDK cleanup succeeding.
  }
};

const storage = {
  getItem(key: string): string | null {
    if (terminated) { return null; }
    const value = canPersistSession && persistentStorage ? persistentStorage.getItem(key) : memory.get(key) ?? null;
    if (key === storageKey && value !== null && canPersistSession) { observedSavedSession = true; }
    return value;
  },
  setItem(key: string, value: string) {
    // A native storage event can arrive after this held refresh finishes.
    if (key === storageKey && observedSavedSession && canPersistSession &&
        persistentStorage?.getItem(storageKey) === null) { clearLocalSession(); }
    // Never silently acknowledge a late refresh/login that cannot be saved.
    if (terminated) { throw new AuthSessionMissingError(); }
    if (canPersistSession && persistentStorage) { persistentStorage.setItem(key, value); }
    else { memory.set(key, value); }
    if (key === storageKey && canPersistSession) { observedSavedSession = true; }
  },
  removeItem(key: string) {
    memory.delete(key);
    persistentStorage?.removeItem(key);
    if (key === storageKey) { observedSavedSession = false; }
  },
};

export const isSessionTerminated = () => terminated;
export const getSessionRemovalError = () => storageError;
// A native notification may arrive after its saved session was removed.
// Qualify it against the owned adapter, including the memory fallback.
export const isCurrentSession = (session: Session | null) => {
  if (terminated) { return false; }
  try {
    const raw = storage.getItem(storageKey);
    const saved: Session | null = raw ? JSON.parse(raw) : null;
    const matches = session === null ? saved === null : !!saved &&
      saved.access_token === session.access_token && saved.user?.id === session.user?.id;
    // Another refresh can reach storage before its older broadcast is delivered.
    // Reject that notification without destroying the current same-user session.
    if (hasAcceptedSession && !matches && session && saved?.user?.id &&
        saved.user.id === session.user?.id &&
        typeof saved.access_token === "string" && saved.access_token.length > 0 &&
        typeof session.access_token === "string" && session.access_token.length > 0) {
      bestEffort(() => supabase.realtime.setAuth(saved.access_token));
      return false;
    }
    if (hasAcceptedSession && (!saved || !matches)) {
      clearLocalSession();
      return false;
    }
    if (matches) { hasAcceptedSession = session !== null; }
    return matches;
  } catch {
    clearLocalSession();
    return false;
  }
};
export const onLocalSignOut = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

// This document cannot sign in again. A fresh login/handoff document creates
// a fresh client; late work in this one must fail toward sign-out.
export const clearLocalSession = (accessToken?: string): string | null => {
  if (terminated) { return logoutToken; }
  try {
    logoutToken = accessToken || JSON.parse(storage.getItem(storageKey) || "null")?.access_token || null;
  } catch {
    logoutToken = accessToken || null;
  }
  terminated = true;
  if (!persistentStorage) {
    storageError = new AuthError("Your browser could not clear the saved sign-in.");
  }
  const keys = [storageKey, `${storageKey}-code-verifier`, `${storageKey}-user`, "accessToken", "refreshToken"];
  for (const getStore of [() => persistentStorage, () => window.sessionStorage]) {
    try {
      const store = getStore();
      if (!store) { throw new Error("Storage unavailable"); }
      // The two generic token names are legacy localStorage entries only.
      for (const key of (store === persistentStorage ? keys : keys.slice(0, 3))) {
        try {
          memory.delete(key);
          store.removeItem(key);
          if (store.getItem(key) !== null) { throw new Error("Session removal failed"); }
        } catch {
          storageError = new AuthError("Your browser could not clear the saved sign-in.");
        }
      }
    } catch {
      storageError = new AuthError("Your browser could not clear the saved sign-in.");
    }
  }
  memory.clear();
  listeners.forEach((listener) => bestEffort(listener));
  bestEffort(() => supabase.auth.stopAutoRefresh());
  bestEffort(() => supabase.realtime.disconnect());
  bestEffort(() => supabase.realtime.setAuth(supabaseAnonKey));
  bestEffort(() => supabase.removeAllChannels());
  return logoutToken;
};

const clientFetch: typeof fetch = (input, init) => {
  const url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url);
  const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
  const logout = url.origin === new URL(supabaseUrl).origin && url.pathname === "/auth/v1/logout" &&
    (init?.method || (input instanceof Request ? input.method : "GET")) === "POST";
  if (terminated && !(logout && logoutToken && headers.get("Authorization") === `Bearer ${logoutToken}`)) {
    return Promise.reject(new AuthSessionMissingError());
  }
  return fetch(input, logout ? { ...init, keepalive: true } : init);
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    detectSessionInUrl: false,
    storageKey,
    storage,
  },
  global: { fetch: clientFetch },
});

// GoTrue broadcasts auth events, but JWT-bound remote logout emits none and
// removing storage does not produce an event in the originating document.
window.addEventListener("storage", (event) => {
  if (event.storageArea === persistentStorage && event.key === storageKey &&
      event.oldValue !== null && event.newValue === null) {
    clearLocalSession();
  }
});
